import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { IconArrowBackUp, IconArrowForwardUp, IconClick } from "@tabler/icons-react";
import { useMasking } from "./useMasking";
import { withCacheBuster } from "@/components/common/adCard/Media";
import { Image } from "@/screens/creatives/types";
import EditImageAction from "../adActions/EditImageAction";
import CreativeInputSection from "../common/CreativeInputSection";
import { AspectRatio } from "../tabs/AspectRatioDropdown";
import { Variations } from "../tabs/VariationsDropdown";
import { Model } from "../tabs/ModelDropdown";
import { File as CustomFile } from "@/types";
import { LibraryMedia } from "@/types";
import { UploadingFile } from "@/components/hoc/fileAttachment/types";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  image: Image | null;
  onEdit: (
    edit_prompt: string,
    mask_file: globalThis.File | undefined,
    original_file: globalThis.File,
    creative_type: "image" | "video",
    aspect_ratio: string | null,
    number_of_media: number,
    avoid_danger_zone: boolean,
    file_ids: string[],
    library_file_ids: string[],
    model?: Model,
  ) => void;
  onEditClick?: () => void;
  // Settings props (synced with CreateDashboard)
  aspectRatio: AspectRatio;
  variations: Variations;
  avoidDangerZone: boolean;
  model: Model;
  onAspectRatioChange: (aspectRatio: AspectRatio) => void;
  onVariationsChange: (variations: Variations) => void;
  onAvoidDangerZoneChange: (avoidDangerZone: boolean) => void;
  onModelChange: (model: Model) => void;
};
const EditImageDialog = ({
  image,
  onOpenChange,
  open,
  onEdit,
  onEditClick,
  // Settings props (synced with CreateDashboard)
  aspectRatio,
  variations,
  avoidDangerZone,
  model,
  onAspectRatioChange,
  onVariationsChange,
  onAvoidDangerZoneChange,
  onModelChange,
}: Props) => {
  const imageUrl = useMemo(() => image?.permalink_url, [image]);

  // Attachments are disabled in edit mode
  const disableAttachmentActions = true;

  // Chat Input states
  const [prompt, setPrompt] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  // creativeType is always "image" in edit mode (creativeTypeDisabled={true})
  const creativeType: "image" | "video" = "image";

  const [files, setFiles] = useState<CustomFile[]>([]);
  const [libraryMedia, setLibraryMedia] = useState<LibraryMedia[]>([]);
  const [uploadingFiles] = useState<UploadingFile[]>([]);
  const [isFileUploading] = useState<boolean>(false);
  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [originalFile, setOriginalFile] = useState<globalThis.File | null>(null);
  const [isLoadingOriginalFile, setIsLoadingOriginalFile] = useState<boolean>(false);

  // Masking hook (needs to be before container sizing to access imgSize)
  const {
    isMaskingEnabled,
    setIsMaskingEnabled,
    strokes,
    redoStack,
    imgSize,
    setImgSize,
    canvasRef,
    imgRef,
    handlePointerDown,
    handlePointerMove,
    finishStroke,
    handleUndo,
    handleRedo,
    getMaskedImageBlob,
  } = useMasking();

  // Container sizing
  const previewContainerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Parse aspect ratio like "9:16" → { rw: 9, rh: 16 }
  const parseAspectRatio = useCallback(
    (ratio?: string | null): { rw: number; rh: number } | null => {
      if (!ratio) return null;
      const parts = ratio.split(":");
      if (parts.length !== 2) return null;
      const rw = Number(parts[0]);
      const rh = Number(parts[1]);
      if (!Number.isFinite(rw) || !Number.isFinite(rh) || rw <= 0 || rh <= 0) return null;
      return { rw, rh };
    },
    [],
  );

  // Compute contained box size for a given ratio within maxW x maxH
  const computeContainedSizeForRatio = useCallback(
    (
      ratio: string | undefined,
      maxW: number,
      maxH: number,
    ): { width: number; height: number } | null => {
      const parsed = parseAspectRatio(ratio);
      if (!parsed) return null;
      const { rw, rh } = parsed;
      const scale = Math.min(maxW / rw, maxH / rh);
      return { width: Math.round(rw * scale), height: Math.round(rh * scale) };
    },
    [parseAspectRatio],
  );

  // Fetch original image when dialog opens (don't upload yet)
  useEffect(() => {
    if (!open || !imageUrl) {
      setOriginalFile(null);
      setIsLoadingOriginalFile(false);
      return;
    }

    // Reset and fetch when dialog opens or image changes
    setOriginalFile(null);
    setIsLoadingOriginalFile(true);
    const abortController = new AbortController();

    const fetchImage = async () => {
      try {
        // Convert imageUrl to blob
        const response = await fetch(withCacheBuster(imageUrl), {
          signal: abortController.signal,
        });
        if (abortController.signal.aborted) return;

        const originalBlob = await response.blob();
        if (abortController.signal.aborted) return;

        const file = new globalThis.File([originalBlob], "original-image.png", {
          type: "image/png",
        });
        if (!abortController.signal.aborted) {
          setOriginalFile(file);
        }
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error fetching original image:", error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoadingOriginalFile(false);
        }
      }
    };

    void fetchImage();

    return () => {
      abortController.abort();
    };
  }, [open, imageUrl]);

  // Compute container size based on preview container dimensions
  useLayoutEffect(() => {
    const compute = () => {
      if (!previewContainerRef.current) return;
      const elem = previewContainerRef.current;
      setContainerSize({
        width: Math.max(0, elem.clientWidth),
        height: Math.max(0, elem.clientHeight),
      });
    };

    if (open) {
      // Initial compute with multiple attempts to ensure ref is available
      const timeoutId = setTimeout(compute, 0);
      const rafId = requestAnimationFrame(() => {
        compute();
      });

      window.addEventListener("resize", compute);
      return () => {
        cancelAnimationFrame(rafId);
        clearTimeout(timeoutId);
        window.removeEventListener("resize", compute);
      };
    }
  }, [open, image, imgSize]);

  const handleEditAd = async () => {
    if (!prompt.trim()) return;
    if (!originalFile) {
      // If original file hasn't been fetched yet, wait for it
      console.error("Original file not ready yet");
      return;
    }

    // Get masked file blob if masking is enabled
    let masked_file: globalThis.File | undefined;
    if (isMaskingEnabled && strokes.length > 0) {
      const maskBlob = await getMaskedImageBlob();
      if (maskBlob) {
        masked_file = new globalThis.File([maskBlob], "masked-image.png", {
          type: "image/png",
        });
      }
    }

    // Prepare file IDs and library file IDs (for additional attachments)
    const file_ids = disableAttachmentActions ? [] : files.map((f) => f.id);
    const library_file_ids = disableAttachmentActions
      ? []
      : libraryMedia.map((m) => m.file_id);

    // Close dialog immediately and pass file blobs to parent
    onOpenChange(false);
    onEdit(
      prompt,
      masked_file,
      originalFile,
      creativeType,
      aspectRatio === "Auto" ? null : aspectRatio,
      variations,
      avoidDangerZone,
      file_ids,
      library_file_ids,
      creativeType === "image" ? model : undefined,
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={true}>
      <DialogContent
        className="max-h-[100vh] h-[100vh] max-w-[70vw] md:max-w-[55vw] px-0 bg-transparent border-0 flex flex-col gap-4 shadow-none"
        hideCloseButton
        onMouseDown={() => onOpenChange(false)}
      >
        {/* Toolbar */}
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="p-2 flex items-center gap-3 bg-white rounded-[12px] w-[90%] self-center"
        >
          <Button
            className={`flex items-center gap-2 text-sm ${
              isMaskingEnabled
                ? "border-[#FF4500] text-[#FF4500] bg-[#FF45000F] hover:bg-[#FF45000F]"
                : ""
            }`}
            onClick={() => setIsMaskingEnabled((prev) => !prev)}
          >
            <IconClick
              className="w-4 h-4"
              color={isMaskingEnabled ? "#FF4500" : "#02185999"}
            />
            <span
              className={`relative top-[1px] text-[12px] font-medium leading-[19.16px] tracking-[0.02em] ${isMaskingEnabled ? "text-[#FF4500]" : "text-brand-primary-800"}`}
            >
              Select area
            </span>
          </Button>

          {/* Undo */}
          {isMaskingEnabled && (
            <>
              <Button size="icon" disabled={strokes.length === 0} onClick={handleUndo}>
                <IconArrowBackUp className="w-4 h-4" color="#021859CC" />
              </Button>

              <Button size="icon" disabled={redoStack.length === 0} onClick={handleRedo}>
                <IconArrowForwardUp className="w-4 h-4" color="#021859CC" />
              </Button>
            </>
          )}

          {/* Cancel editing */}
          <Button
            className="ml-auto text-brand-primary-new font-medium text-[12px] leading-[19.16px] tracking-[0.02em]"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
        </div>

        {/* Image Preview Section */}
        <div
          ref={previewContainerRef}
          className="flex-1 flex items-center justify-center min-h-0 mb-4"
        >
          {imageUrl && containerSize && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              className="group relative overflow-hidden"
              style={{
                width: `${(() => {
                  const rectW = containerSize.width - 8;
                  const rectH = containerSize.height - 8;
                  const ar = image?.aspect_ratio;
                  const size = computeContainedSizeForRatio(ar, rectW, rectH);
                  return size?.width ?? rectW;
                })()}px`,
                height: `${(() => {
                  const rectW = containerSize.width - 8;
                  const rectH = containerSize.height - 8;
                  const ar = image?.aspect_ratio;
                  const size = computeContainedSizeForRatio(ar, rectW, rectH);
                  return size?.height ?? rectH;
                })()}px`,
              }}
            >
              <img
                ref={imgRef}
                src={withCacheBuster(imageUrl)}
                alt="Creative to edit"
                crossOrigin="anonymous"
                className="w-full h-full object-contain select-none"
                draggable={false}
                onLoad={(e) =>
                  setImgSize({
                    width: e.currentTarget.naturalWidth,
                    height: e.currentTarget.naturalHeight,
                  })
                }
                loading="eager"
                decoding="sync"
              />
              {!isMaskingEnabled && (
                <div className="absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <EditImageAction
                    onClick={() => {
                      if (onEditClick) {
                        onEditClick();
                      }
                    }}
                  />
                </div>
              )}

              {/* Canvas overlay is present but invisible & non-interactive when masking is OFF */}
              {imgSize && (
                <canvas
                  ref={canvasRef}
                  width={imgSize.width}
                  height={imgSize.height}
                  className="absolute inset-0 w-full h-full"
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={finishStroke}
                  onPointerLeave={finishStroke}
                  style={{
                    opacity: isMaskingEnabled ? 1 : 0,
                    pointerEvents: isMaskingEnabled ? "auto" : "none",
                    cursor: isMaskingEnabled
                      ? `url('data:image/svg+xml,<svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg"><foreignObject x="-4" y="-4" width="56" height="56"><div xmlns="http://www.w3.org/1999/xhtml" style="backdrop-filter:blur(2px);clip-path:url(%23bgblur_0_34322_55792_clip_path);height:100%;width:100%"></div></foreignObject><circle data-figma-bg-blur-radius="4" cx="24" cy="24" r="24" fill="%23E3E3E3" fill-opacity="0.4"/><defs><clipPath id="bgblur_0_34322_55792_clip_path" transform="translate(4 4)"><circle cx="24" cy="24" r="24"/></clipPath></defs></svg>') 24 24, auto`
                      : "default",
                  }}
                />
              )}
            </div>
          )}
        </div>

        {/* Input Section */}
        <div onMouseDown={(e) => e.stopPropagation()}>
          <CreativeInputSection
            prompt={prompt}
            creativeType={creativeType}
            aspectRatio={aspectRatio}
            variations={variations}
            avoidDangerZone={avoidDangerZone}
            model={model}
            files={files}
            libraryMedia={libraryMedia}
            uploadingFiles={uploadingFiles}
            isFileUploading={isFileUploading}
            submitting={isLoadingOriginalFile}
            disabled={!!imageUrl && !originalFile}
            popoverOpen={popoverOpen}
            onPromptChange={setPrompt}
            onCreativeTypeChange={() => {}}
            showCreativeTypeDropdown={false}
            onAspectRatioChange={onAspectRatioChange}
            onVariationsChange={onVariationsChange}
            onAvoidDangerZoneChange={onAvoidDangerZoneChange}
            onModelChange={onModelChange}
            onFileChange={() => {}}
            onLibraryAttachmentChange={setLibraryMedia}
            onRemoveFile={(file) =>
              setFiles((prev) => prev.filter((f) => f.id !== file.id))
            }
            onRemoveLibraryAttachment={(file) =>
              setLibraryMedia((prev) =>
                prev.filter(
                  (m) =>
                    (m.file_id && m.file_id !== file.id) ||
                    (!m.file_id && m.file_url !== file.public_url),
                ),
              )
            }
            onSubmit={handleEditAd}
            onPopoverOpenChange={setPopoverOpen}
            fileInputRef={fileInputRef}
            creativeTypeDisabled={true}
            disableAttachmentActions={disableAttachmentActions}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditImageDialog;
